import { ArpMediaType } from '@arpeggi/shared'
import { Box } from '@arpeggi/ui'
import { FC, useMemo, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { deserializeSongFromStorage } from '../../../stores/song/SongStore'
import { getSongOpenseaLink } from '../../../utils/media'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Row } from '../../core/Components'
import { formatEpochTimestamp } from '../../core/helpers/dates'
import { prettyPrintWalletAddress } from '../../core/Utils'
import Contributors from '../../platform/explore/common/Contributors'
import { useAsyncEffect } from '../../platform/explore/common/utils'
import { formatTime } from '../../player/PlayerUtils'
import { InfoRow } from '../../profile/media-content/ProfileCurrentlyPlaying'
import { MediaInfoButtons, MediaInfoColumn, MediaInfoTitle } from '../shared/MediaInfo'
import { Column } from '../SongTable'

export const useSongInfo = ({ tokenId }: { tokenId: number }) => {
  const [openseaLink, setOpenseaLink] = useState<string>(null)
  const [data, setData] = useState<IArpMedia>(null)

  useAsyncEffect(async () => {
    const song = await ArpRegistry.getByIdAsync(tokenId)
    setData(song)
    const link = await getSongOpenseaLink(tokenId)
    setOpenseaLink(link)
  }, [tokenId])

  const arp = useMemo(() => {
    if (!data) return null
    return deserializeSongFromStorage(data.metadata.arpeggiComposition)
  }, [data])
  const songLength = arp && formatTime(arp.lengthInSeconds)

  const createTime = data?.createdAt && parseInt(data.createdAt)
  const mintDate = createTime && formatEpochTimestamp(createTime, 'MM/DD/YY')
  const isReady = data !== null

  return { song: data, isReady, songLength, mintDate, openseaLink }
}

interface SongInfoDisplayProps {
  title: string
  artistEthAddress: string
  artistName: string
  contractAddress?: string
  tokenId: number
  genre: string
  songLength?: string
  mintDate: string
}

export const SongInfoDisplay: FC<SongInfoDisplayProps> = ({
  title,
  artistEthAddress,
  artistName,
  contractAddress,
  tokenId,
  genre,
  songLength,
  mintDate
}) => (
  <Box css={{ width: '100%' }}>
    <MediaInfoTitle title={title} artistEthAddress={artistEthAddress} artistName={artistName} />
    <Row css={{ fontSize: '14px', marginTop: '50px' }}>
      <MediaInfoColumn title="Overview" css={{ flex: '1', marginRight: '25px' }}>
        <InfoRow title="Contract Address" tight>
          {contractAddress ? prettyPrintWalletAddress(contractAddress) : '-'}
        </InfoRow>
        <InfoRow title="Token ID" tight>
          {tokenId > 0 ? tokenId : '-'}
        </InfoRow>
        <InfoRow title="Blockchain" tight>
          Polygon
        </InfoRow>
      </MediaInfoColumn>
      <MediaInfoColumn title="Song properties" css={{ flex: '1' }}>
        <InfoRow title="Genre" tight>
          {genre}
        </InfoRow>
        <InfoRow title="Duration" tight>
          {songLength ?? '-'}
        </InfoRow>
        <InfoRow title="Mint date" tight>
          {mintDate}
        </InfoRow>
      </MediaInfoColumn>
    </Row>
  </Box>
)

interface Props {
  index: number
}

const SongInfo: FC<Props> = ({ index }) => {
  const { song, isReady, songLength, mintDate, openseaLink } = useSongInfo({ tokenId: index })

  if (!isReady) {
    return <CenteredLoadingAnimation />
  }

  return (
    <Column css={{ width: '100%' }}>
      <MediaInfoTitle
        title={song.metadata.title}
        artistEthAddress={song.user.walletAddress}
        artistName={song.user.displayName}
      />
      <Row
        css={{
          fontSize: '14px',
          marginTop: '50px',
          flexDirection: 'column',
          '@tablet': { flexDirection: 'row' }
        }}
      >
        <MediaInfoColumn title="Overview" css={{ flex: '1', '@tablet': { marginRight: '25px' } }}>
          <InfoRow title="Contract Address" tight>
            {prettyPrintWalletAddress(song.contractAddress || '')}
          </InfoRow>
          <InfoRow title="Token ID" tight>
            {song.tokenId}
          </InfoRow>
          <InfoRow title="Blockchain" tight>
            Polygon
          </InfoRow>
        </MediaInfoColumn>
        <MediaInfoColumn title="Song properties" css={{ flex: '1' }}>
          <InfoRow title="Genre" tight>
            {song.metadata.genres[0]}
          </InfoRow>
          <InfoRow title="Duration" tight>
            {songLength}
          </InfoRow>
          <InfoRow title="Mint date" tight>
            {mintDate}
          </InfoRow>
        </MediaInfoColumn>
      </Row>
      <Row>
        <MediaInfoColumn title="Contributors">
          <Contributors
            index={index}
            type={ArpMediaType.SONG}
            noText
            avatarSize="38px"
            maxContributors={10}
            css={{ marginTop: '0px' }}
          />
          <MediaInfoButtons type={ArpMediaType.SONG} index={index} openseaLink={openseaLink} />
        </MediaInfoColumn>
      </Row>
    </Column>
  )
}

export default SongInfo
